import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import FaceRetouchingNaturalOutlinedIcon from "@mui/icons-material/FaceRetouchingNaturalOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import FlowStepCard from "../components/FlowStepCard";

const FLOW_STEPS = [
  {
    step: 1,
    title: "Enter your details",
    description: "Provide the basic information needed to start a verification session.",
    icon: BadgeOutlinedIcon,
  },
  {
    step: 2,
    title: "Scan your ID document",
    description: "Capture a photo or upload an image of a government-issued ID document.",
    icon: DocumentScannerOutlinedIcon,
  },
  {
    step: 3,
    title: "Take a selfie",
    description: "A live selfie is captured and matched against the document photo.",
    icon: FaceRetouchingNaturalOutlinedIcon,
  },
  {
    step: 4,
    title: "Review the result",
    description: "The verification outcome is shown once the checks have completed.",
    icon: TaskAltOutlinedIcon,
  },
];

function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("verify") === "true") {
      // Forward profileCode/jurisdiction (if present) so a link like
      // /?verify=true&profileCode=...&jurisdiction=... carries them through
      // to the verification page that actually starts the session.
      const forwarded = new URLSearchParams();
      const profileCode = searchParams.get("profileCode");
      const jurisdiction = searchParams.get("jurisdiction");
      if (profileCode) forwarded.set("profileCode", profileCode);
      if (jurisdiction) forwarded.set("jurisdiction", jurisdiction);

      const query = forwarded.toString();
      navigate(`/verification${query ? `?${query}` : ""}`, { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2.5, md: 4 },
        }}
      >
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <VerifiedUserOutlinedIcon color="primary" fontSize="large" />
            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.5rem", sm: "1.875rem", md: "2.125rem" } }}
            >
              ACT KYC Demo Portal
            </Typography>
          </Stack>
          <Typography variant="subtitle1" color="primary.main" fontWeight={600}>
            Built for Compliance. Powered by Integrity.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: "78ch" }}>
            This portal is a live demonstration of ACT&apos;s identity verification (KYC)
            experience. Click &quot;Start Verification&quot; below to try the full flow yourself —
            document capture, a selfie check, and a real-time result — in a safe, sandboxed demo
            environment.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/verification")}
          sx={{
            flexShrink: 0,
            whiteSpace: "nowrap",
            alignSelf: { xs: "stretch", md: "center" },
          }}
        >
          Start Verification
        </Button>
      </Paper>

      <Stack spacing={1.5}>
        <Typography variant="h6" fontWeight={700}>
          Demo instructions
        </Typography>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack
            component="ol"
            spacing={1.25}
            sx={{ pl: 3, m: 0, "& li": { color: "text.secondary" } }}
          >
            <Typography component="li" variant="body2">
              Click <strong>Start Verification</strong> to open the demo verification flow.
            </Typography>
            <Typography component="li" variant="body2">
              No real identity documents are required — any ID-shaped document or test image works
              for demo purposes.
            </Typography>
            <Typography component="li" variant="body2">
              Allow camera access when prompted, so the demo can capture your document and selfie.
            </Typography>
            <Typography component="li" variant="body2">
              The flow runs in a sandboxed demo environment — no verification data from this portal
              is used for real KYC decisions.
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="h6" fontWeight={700}>
          Supported verification flow
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          {FLOW_STEPS.map((flowStep) => (
            <FlowStepCard key={flowStep.step} {...flowStep} />
          ))}
        </Box>
      </Stack>

      <Divider />

      <Typography variant="body2" color="text.secondary">
        Ready to try it yourself?{" "}
        <Button variant="text" size="small" onClick={() => navigate("/verification")}>
          Start Verification
        </Button>
      </Typography>
    </Stack>
  );
}

export default HomePage;
